import {
  DrawerMenuItemAction,
  DrawerMenuItemActionType,
  DrawerMenuItemNestedSubmenuType,
  DrawerMenuItemSubmenuItemType,
  DrawerMenuItemSubmenuLink,
  DrawerMenuItemSubmenuType,
  Icon,
  useColorScheme,
  useHover,
  useLanguage,
  useLocales,
} from "@/lib/main";
import { CSSProperties, useEffect, useState } from "react";
import { useLocation } from "react-router";

type Props = {
  drawerMenuItem: DrawerMenuItemSubmenuType;
  isDrawerOpen: boolean;
  isHashLinks?: boolean;
  level?: number;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
  openSubmenuId?: string | null;
  setOpenSubmenuId?: (id: string | null) => void;
};

export default function DrawerMenuItemSubmenu({
  drawerMenuItem,
  isDrawerOpen,
  isHashLinks,
  level = 1,
  setIsDrawerOpen,
  openSubmenuId,
  setOpenSubmenuId,
}: Props) {
  const location = useLocation();
  const l = useLocales();
  const { textDirection } = useLanguage();
  const { colorScheme } = useColorScheme();
  const { isHovered, onMouseEnter, onMouseLeave } = useHover();

  // Level-1 submenus use external accordion state; nested levels use local state.
  const isAccordionControlled =
    level === 1 &&
    openSubmenuId !== undefined &&
    setOpenSubmenuId !== undefined;
  const [localShowSubmenu, setLocalShowSubmenu] = useState(false);
  const showSubmenu = isAccordionControlled
    ? openSubmenuId === drawerMenuItem.id
    : localShowSubmenu;

  const currentLocation = isHashLinks
    ? location.pathname + location.hash
    : location.pathname;

  const hasActiveSubitem = (
    items: DrawerMenuItemSubmenuItemType[],
  ): boolean => {
    return items.some((item): boolean => {
      if ("to" in item) {
        return currentLocation === item.to;
      }

      if ("submenu" in item) {
        return hasActiveSubitem(item.submenu);
      }

      return false;
    });
  };

  const isActive = hasActiveSubitem(drawerMenuItem.submenu);

  const handleClick = () => {
    if (isAccordionControlled) {
      setOpenSubmenuId!(showSubmenu ? null : drawerMenuItem.id);
    } else {
      setLocalShowSubmenu(!localShowSubmenu);
    }
  };

  useEffect(() => {
    if (!isDrawerOpen && !isAccordionControlled) {
      const frame = window.requestAnimationFrame(() => {
        setLocalShowSubmenu(false);
      });

      return () => {
        window.cancelAnimationFrame(frame);
      };
    }
  }, [isDrawerOpen, isAccordionControlled]);

  const isNestedItem = (
    item: DrawerMenuItemSubmenuItemType,
  ): item is DrawerMenuItemNestedSubmenuType => "submenu" in item;

  const isActionItem = (
    item: DrawerMenuItemSubmenuItemType,
  ): item is DrawerMenuItemActionType => "action" in item;

  const rowPadding =
    level <= 1
      ? "var(--page-padding-horizontal)"
      : `calc(var(--page-padding-horizontal) + (${level - 1} * var(--spacing-2xl)))`;

  const leadingIconName = drawerMenuItem.icon
    ? drawerMenuItem.icon
    : "circle-solid";

  const liStyle: CSSProperties = {
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",

    width: "100%",

    borderBottom: !showSubmenu
      ? "var(--border-width-xs) solid var(--color-border-subtle)"
      : undefined,
    backgroundColor: isHovered
      ? colorScheme === "dark"
        ? "var(--color-background-frame)"
        : "var(--color-background-canvas)"
      : "transparent",
  };

  const containerStyle: CSSProperties = {
    display: "flex",
    paddingInlineStart: rowPadding,
    paddingInlineEnd: rowPadding,
    width: "100%",
  };

  const linkStyle: CSSProperties = {
    display: "flex",
    gap: "var(--spacing-sm)",
    alignItems: "center",

    padding: "var(--spacing-md) 0",
    width: "100%",

    color: isActive ? "var(--color-text-link)" : "inherit",
    fontSize: "var(--font-size-drawer-item)",
  };

  const chevronContainerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    transform: !showSubmenu
      ? "rotate(0deg)"
      : textDirection === "rtl"
        ? "rotate(180deg)"
        : "rotate(-180deg)",
    transition: "transform 0.5s ease-in-out",
  };

  const submenuContainerStyle: CSSProperties = {
    borderTop: showSubmenu
      ? "var(--border-width-xs) solid var(--color-border-subtle)"
      : undefined,
    display: "grid",
    gridTemplateRows: showSubmenu ? "1fr" : "0fr",
    opacity: showSubmenu ? 1 : 0,
    overflow: "hidden",
    transition: "grid-template-rows 0.5s ease-in-out, opacity 0.5s ease-in-out",
  };

  const submenuStyle: CSSProperties = {
    listStyle: "none",
    margin: 0,
    padding: 0,
    minHeight: 0,
    overflow: "hidden",
  };

  return (
    <>
      <li style={liStyle}>
        <div
          style={containerStyle}
          onClick={handleClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <span className="font-color-nav" style={linkStyle}>
            <Icon
              name={leadingIconName}
              color={isActive ? "active" : "secondary"}
              size={
                // (level >= 2 && leadingIconName === "circle-solid") ||
                // (level >= 2 && leadingIconName === "circle")
                level >= 2 ? "xs" : "md"
              }
            />

            {l(drawerMenuItem.title)}
          </span>

          <div style={chevronContainerStyle}>
            <Icon
              color={isActive ? "active" : "secondary"}
              name="chevron-down"
              size="md"
            />
          </div>
        </div>

        <div style={submenuContainerStyle}>
          <ul style={submenuStyle}>
            {drawerMenuItem.submenu.map((submenuItem) =>
              isNestedItem(submenuItem) ? (
                <DrawerMenuItemSubmenu
                  key={submenuItem.id}
                  drawerMenuItem={{
                    ...submenuItem,
                    kind: "submenu",
                  }}
                  isDrawerOpen={isDrawerOpen}
                  isHashLinks={isHashLinks}
                  level={level + 1}
                  setIsDrawerOpen={setIsDrawerOpen}
                />
              ) : isActionItem(submenuItem) ? (
                <DrawerMenuItemAction
                  key={submenuItem.id}
                  drawerMenuItem={submenuItem}
                  level={level + 1}
                  setIsDrawerOpen={setIsDrawerOpen}
                />
              ) : (
                <DrawerMenuItemSubmenuLink
                  key={submenuItem.id}
                  isHashLinks={isHashLinks}
                  level={level + 1}
                  submenuItem={submenuItem}
                  setIsDrawerOpen={setIsDrawerOpen}
                />
              ),
            )}
          </ul>
        </div>
      </li>
    </>
  );
}
